/**
 * The DOCX export's dialog and status (decisions 7, 14, 60; part 22, tables K″ and Y). The dialog picks the look: the
 * book theme or the Manuscript in one of its two faces (Y2); the status sits under the toolbar while an export runs or
 * has something to say, never over the text, as the PDF's and the EPUB's do: its progress and Cancel; then the file to
 * save, with the images it couldn't take and a base it couldn't keep (Y5), or what went wrong with Try again.
 */
import type { DocxPreset } from '@cockatrice/export/docx';
import { createSignal } from 'solid-js';
import type { DocxState } from '../docx/controller.ts';
import { focusOn } from './announce.ts';
import { el, when } from '../ui/dom.ts';
import { ModalBody, ModalButtons } from './modal.ts';
import Button from '../ui/Button.ts';
import RadioGroup from '../ui/RadioGroup.ts';
import { component } from '../ui/solid.ts';
import type { Child } from '../ui/dom.ts';
import { JobProgress, JobStatus } from './job-status.ts';
import type { JobCount } from './job-status.ts';
import type { Shell } from './shell.ts';

/** A look the dialog offers: its control's ID, its preset and its name. */
interface Look {
  readonly id: string;
  readonly preset: DocxPreset;
  readonly name: 'docx.theme' | 'docx.times' | 'docx.courier';
}

/** Y2: the theme, checked when the dialog opens. */
const THEME_LOOK: Look = { id: 'theme', preset: { kind: 'theme' }, name: 'docx.theme' };

/** Y2: the dialog's three looks, in order. */
const LOOKS: readonly Look[] = [
  THEME_LOOK,
  { id: 'times', preset: { kind: 'manuscript', font: 'times-new-roman' }, name: 'docx.times' },
  { id: 'courier', preset: { kind: 'manuscript', font: 'courier-new' }, name: 'docx.courier' },
];

/** What the status says (K″-rows' outcomes): reading, how far the document has come, or putting the file together. */
export function docxText(shell: Shell, state: DocxState): string {
  const { t } = shell;
  switch (state.kind) {
    case 'idle':
      return '';
    case 'reading':
      return t('docx.reading');
    case 'exporting':
      return state.done >= state.total ? t('docx.finishing') : t('docx.progress', { percent: Math.floor((100 * state.done) / state.total) });
    case 'ready':
      return t('docx.ready');
    case 'failed':
      return state.reason === 'unreadable' ? t('docx.unreadable') : t('docx.failed');
  }
}

/** K″12, K″13, Y5: the images a finished DOCX left out, and a base it couldn't keep, in words; empty for neither. */
function notesText(shell: Shell, state: DocxState): string[] {
  if (state.kind !== 'ready') return [];
  return [state.missing > 0 ? shell.t('docx.missing', { count: state.missing }) : '', state.kept ? '' : shell.t('docx.notKept')].filter((line) => line !== '');
}

/**
 * Part 11c, A13: what the export announces: its phases (reading, making, finishing, ready, failed), never each percent the
 * status shows; a finished DOCX says with it what it left out (Y5). Making starts before the first chapter.
 */
export function docxNews(shell: Shell, state: DocxState): string {
  if (state.kind === 'exporting' && (state.done < state.total || state.total === 0)) return shell.t('docx.making');
  return [docxText(shell, state), ...notesText(shell, state)].filter((line) => line !== '').join(' ');
}

/**
 * Y2: the dialog's body: the looks as the library's radio cards in a labelled group, the Manuscript's description tied to
 * its two, Cancel and Export. Export closes the dialog and starts the export, the focus on its Cancel (A24); Cancel
 * closes it, and the dialog gives the focus back to Export DOCX….
 */
export function DocxChoice(shell: Shell): Child {
  const { t, ui, docx } = shell;
  const [chosen, setChosen] = createSignal(THEME_LOOK);
  return [
    ModalBody(
      {},
      component(RadioGroup, {
        class: 'docx-looks',
        name: 'docx-look',
        label: t('docx.look'),
        variant: 'cards',
        get value() {
          return chosen().id;
        },
        options: LOOKS.map((look) => ({
          value: look.id,
          label: t(look.name),
          autofocus: look === THEME_LOOK,
          describedBy: look.preset.kind === 'manuscript' ? 'docx-manuscript' : undefined,
        })),
        // A radio sends `change` only as it is checked: the look checked is chosen.
        onChange: (id: string) => {
          for (const look of LOOKS) if (look.id === id) setChosen(look);
        },
      }),
      el('p', { id: 'docx-manuscript', class: 'docx-hint' }, t('docx.manuscriptHint')),
    ),
    ModalButtons(
      component(Button, { variant: 'secondary', onClick: () => ui.setDialog(null), children: t('docx.close') }),
      component(Button, {
        variant: 'primary',
        class: 'start-docx',
        onClick: () => {
          ui.setDialog(null);
          docx.start(chosen().preset);
          focusOn(shell, '.cancel-docx');
        },
        children: t('docx.export'),
      }),
    ),
  ];
}

/**
 * Y3, Y4: the status region, shown while the export isn't idle; what it announces is {@link docxNews}, said by the
 * workspace's announcers (A13). Each button leaves the focus where the next step is (A24): Cancel and Dismiss on the
 * toolbar's Export DOCX…, Try again on the new Cancel.
 */
export function DocxStatus(shell: Shell): Child {
  const { t, docx } = shell;
  const state = (): DocxState => docx.state();
  const running = (): boolean => state().kind === 'reading' || state().kind === 'exporting';
  const count = (): JobCount => {
    const now = state();
    return now.kind === 'exporting' ? now : null;
  };
  return [
    when(
      () => state().kind !== 'idle',
      () =>
        JobStatus({
          class: 'docx-status',
          label: t('docx.label'),
          tone: () => (state().kind === 'failed' ? 'danger' : state().kind === 'ready' ? (notesText(shell, state()).length === 0 ? 'success' : 'warn') : 'neutral'),
          children: [
            el('p', {}, () => docxText(shell, state())),
            () => notesText(shell, state()).map((line) => el('p', { class: 'docx-note' }, line)),
            JobProgress(t('docx.label'), count),
          ],
          actions: [
            when(running, () =>
              component(Button, {
                size: 'sm',
                class: 'cancel-docx',
                onClick: () => {
                  docx.cancel();
                  focusOn(shell, '.export-docx');
                },
                children: t('docx.cancel'),
              }),
            ),
            () => {
              const now = state();
              return now.kind === 'ready' ? component(Button, { size: 'sm', class: 'save-docx', href: now.url, download: now.name, children: t('docx.save') }) : null;
            },
            when(
              () => state().kind === 'failed',
              () =>
                component(Button, {
                  size: 'sm',
                  onClick: () => {
                    docx.retry();
                    focusOn(shell, '.cancel-docx');
                  },
                  children: t('docx.retry'),
                }),
            ),
            when(
              () => !running(),
              () =>
                component(Button, {
                  variant: 'ghost',
                  size: 'sm',
                  onClick: () => {
                    docx.dismiss();
                    focusOn(shell, '.export-docx');
                  },
                  children: t('docx.dismiss'),
                }),
            ),
          ],
        }),
    ),
  ];
}
